import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { userAPI, temporaryLocationsAPI, visibilityAPI } from '../services/api';
import { TemporaryLocation } from '../types';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  // View mode: 'private' = ma vue complète, 'public' = ce que mes amis voient
  const [viewMode, setViewMode] = useState<'private' | 'public'>('private');
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
  });
  const [addressData, setAddressData] = useState({
    street_address: user?.address?.street_address || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    postal_code: user?.address?.postal_code || '',
    country: user?.address?.country || '',
    apartment_number: user?.address?.apartment_number || '',
    access_code: user?.address?.access_code || '',
  });

  // Temporary locations state
  const [tempLocations, setTempLocations] = useState<TemporaryLocation[]>([]);

  // Visibility state
  const [visibilityLevel, setVisibilityLevel] = useState<number>(4); // Default to "Numéro"
  const [savingVisibility, setSavingVisibility] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
      });
      setAddressData({
        street_address: user.address?.street_address || '',
        city: user.address?.city || '',
        state: user.address?.state || '',
        postal_code: user.address?.postal_code || '',
        country: user.address?.country || '',
        apartment_number: user.address?.apartment_number || '',
        access_code: user.address?.access_code || '',
      });
      loadTempLocations();
      loadVisibilitySettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadVisibilitySettings = async () => {
    if (!user?.address?.id) return;
    try {
      const settings = await visibilityAPI.getSettings('permanent', user.address.id);
      setVisibilityLevel(settings.default_visibility_level);
    } catch (error) {
      console.error('Failed to load visibility settings:', error);
    }
  };

  const handleVisibilityChange = async (newLevel: number) => {
    if (!user?.address?.id) return;

    setVisibilityLevel(newLevel);
    setSavingVisibility(true);

    try {
      await visibilityAPI.updateDefaultLevel('permanent', user.address.id, newLevel);
      showMessage('Niveau de visibilité mis à jour');
    } catch (error) {
      console.error('Failed to update visibility:', error);
      showMessage('Erreur lors de la mise à jour');
    } finally {
      setSavingVisibility(false);
    }
  };

  const loadTempLocations = async () => {
    try {
      // Load all temporary locations (active, upcoming, and past)
      const { temporaryLocations } = await temporaryLocationsAPI.getUserTemporaryLocations();
      // Sort by start_datetime in descending order (most recent first)
      const sorted = temporaryLocations.sort((a, b) =>
        new Date(b.start_datetime).getTime() - new Date(a.start_datetime).getTime()
      );
      setTempLocations(sorted);
    } catch (error) {
      console.error('Failed to load temporary locations:', error);
    }
  };

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddressData({
      ...addressData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showMessage('File size must be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      const { photo_url } = await userAPI.uploadPhoto(file);

      // Update user context with new photo
      if (user) {
        updateUser({ ...user, profile_photo: photo_url });
      }

      showMessage('Photo uploaded successfully!');
    } catch (error) {
      showMessage('Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre photo de profil ?')) return;

    try {
      setLoading(true);
      await userAPI.deletePhoto();

      // Update user context to remove photo
      if (user) {
        updateUser({ ...user, profile_photo: undefined });
      }

      showMessage('Photo supprimée avec succès');
      setShowPhotoMenu(false);
    } catch (error) {
      showMessage('Échec de la suppression de la photo');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Update profile
      await userAPI.updateProfile(profileData);

      // Update address (main fields)
      await userAPI.updateAddress({
        street_address: addressData.street_address,
        city: addressData.city,
        state: addressData.state,
        postal_code: addressData.postal_code,
        country: addressData.country,
      });

      // Update complement fields (apartment, code)
      await userAPI.updateAddressPrivacy({
        apartment_number: addressData.apartment_number || '',
        access_code: addressData.access_code || '',
      });

      // Refresh user data
      const { user: updatedUser } = await userAPI.getUserById(user!.id);
      updateUser(updatedUser);

      showMessage('Profil mis à jour !');
      setEditing(false);
    } catch (error) {
      showMessage('Échec de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleDeleteTempLocation = async (id: number) => {
    if (!window.confirm('Supprimer ce lieu temporaire?')) return;

    try {
      setLoading(true);
      await temporaryLocationsAPI.delete(id);
      await loadTempLocations();
      showMessage('Lieu temporaire supprimé');
    } catch (error) {
      showMessage('Échec de la suppression du lieu temporaire');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTempLocation = (id: number) => {
    // Find the temp location to edit
    const loc = tempLocations.find(l => l.id === id);
    if (loc) {
      // Store data in localStorage to preserve it when redirecting to Map
      localStorage.setItem('editingTempLocation', JSON.stringify(loc));
      localStorage.setItem('returnToProfile', 'true');
      navigate(`/map?editTempLocation=${id}`);
    }
  };

  const handleCreateNewTempLocation = () => {
    // Redirect to map to create new temp location
    navigate('/map?createTempLocation=true');
  };

  const getLocationTypeInfo = (type: string) => {
    const types: { [key: string]: { label: string; icon: string; color: string } } = {
      vacation: { label: 'Vacances', icon: '🏖️', color: '#ff6b6b' },
      party: { label: 'Soirée', icon: '🎉', color: '#a29bfe' },
      weekend: { label: 'Weekend', icon: '🎭', color: '#74b9ff' },
      event: { label: 'Événement', icon: '🎪', color: '#fd79a8' },
      other: { label: 'Autre', icon: '📍', color: '#636e72' },
    };
    return types[type] || types['other'];
  };

  const getTimeStatus = (startDate: Date, endDate: Date): { label: string; color: string } => {
    const now = new Date();

    if (now < startDate) {
      return { label: 'À venir', color: '#3498db' }; // Bleu
    } else if (now > endDate) {
      return { label: 'Passé', color: '#95a5a6' }; // Gris
    } else {
      return { label: 'En cours', color: '#28a745' }; // Vert - même couleur que le toggle
    }
  };

  const getPhotoUrl = (photoPath?: string) => {
    if (!photoPath) return null;
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl.replace(/\/api$/, '')}${photoPath}`;
  };

  // Format date avec mois abrégé et sans année si année en cours
  const formatDate = (date: Date) => {
    const now = new Date();
    const isCurrentYear = date.getFullYear() === now.getFullYear();
    const day = date.getDate();
    const monthShort = date.toLocaleString('fr-FR', { month: 'short' }).replace('.', '');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    if (isCurrentYear) {
      return `${day} ${monthShort} ${time}`;
    }
    return `${day} ${monthShort} ${year} ${time}`;
  };

  // Calculer la durée entre deux dates
  const calculateDuration = (start: Date, end: Date) => {
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diffDays > 0) {
      return `${diffDays}j ${diffHours}h`;
    }
    return `${diffHours}h`;
  };

  const [menuOpenId, setMenuOpenId] = React.useState<number | null>(null);
  const [showPhotoMenu, setShowPhotoMenu] = React.useState(false);
  const [showPhotoModal, setShowPhotoModal] = React.useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Close photo menu if clicking outside
      if (showPhotoMenu && !target.closest('.photo-wrapper-container')) {
        setShowPhotoMenu(false);
      }

      // Close location menu if clicking outside
      if (menuOpenId !== null && !target.closest('.card-menu')) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPhotoMenu, menuOpenId]);

  if (!user) return null;

  return (
    <div className="profile-page">
      <div className="profile-container">
        <h1>Yahou !</h1>

        {/* View Mode Toggle - Discreet corner button */}
        <button
          className={`view-mode-corner-toggle ${viewMode === 'public' ? 'public' : ''}`}
          onClick={() => {
            setViewMode(viewMode === 'private' ? 'public' : 'private');
            if (editing) setEditing(false);
          }}
          title={viewMode === 'private' ? 'Voir comme mes amis' : 'Retour à ma vue'}
        >
          {viewMode === 'private' ? '👁️' : '👤'}
          <span className="view-mode-corner-label">
            {viewMode === 'private' ? 'Vue publique' : 'Ma vue'}
          </span>
        </button>

        {viewMode === 'public' && (
          <div className="view-mode-banner">
            👁️ Vous voyez votre profil tel qu'il apparaît à vos amis
          </div>
        )}

        {message && <div className="success-message">{message}</div>}

        <div className="profile-photo-section">
          <div className="photo-wrapper-container">
            <div
              className="photo-wrapper"
              onClick={() => viewMode === 'private' && user.profile_photo && setShowPhotoMenu(!showPhotoMenu)}
              style={{ cursor: viewMode === 'private' && user.profile_photo ? 'pointer' : 'default' }}
            >
              {user.profile_photo ? (
                <img
                  src={getPhotoUrl(user.profile_photo) || undefined}
                  alt="Profile"
                  className="profile-photo"
                />
              ) : (
                <div className="profile-photo-placeholder">
                  {user.first_name[0]}{user.last_name[0]}
                </div>
              )}
            </div>
            {viewMode === 'private' && user.profile_photo && showPhotoMenu && (
              <div className="photo-menu-dropdown">
                <button onClick={() => { setShowPhotoModal(true); setShowPhotoMenu(false); }}>
                  Afficher la photo de profil
                </button>
                <button onClick={() => { document.getElementById('photo-upload')?.click(); setShowPhotoMenu(false); }}>
                  Modifier la photo de profil
                </button>
                <button onClick={handleDeletePhoto} className="delete-option">
                  Supprimer ma photo de profil
                </button>
              </div>
            )}
          </div>
          {viewMode === 'private' && !user.profile_photo && (
            <label htmlFor="photo-upload" className="btn-secondary">
              {loading ? t('profile.uploading') : t('profile.changePhoto')}
            </label>
          )}
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
            disabled={loading}
          />
        </div>

        {/* Photo Modal */}
        {showPhotoModal && (
          <div className="photo-modal-overlay" onClick={() => setShowPhotoModal(false)}>
            <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="photo-modal-close" onClick={() => setShowPhotoModal(false)}>
                ✕
              </button>
              <img
                src={getPhotoUrl(user.profile_photo) || undefined}
                alt="Profile"
                className="photo-modal-img"
              />
            </div>
          </div>
        )}

        <div className="profile-info">
          <h2 className="user-name">{user.first_name} {user.last_name}</h2>

          {/* Email and phone displayed directly under name */}
          {!editing && (
            <div className="user-contact-info">
              <p className="contact-info-text">{user.email}</p>
              {profileData.phone && (
                <p className="contact-info-text">{profileData.phone}</p>
              )}
            </div>
          )}

          {!editing ? (
            // MODE LECTURE
            <div className="profile-display">
              {viewMode === 'private' ? (
                // VUE PRIVÉE - Toutes les infos
                <>
                  {/* Address Section with Visibility Slider */}
                  <div className="address-visibility-section">
                    <div className="info-section-title">Mon adresse permanente :</div>

                    {/* Full address - always visible to user */}
                    <div className="my-full-address">
                      <p className="address-line-main">
                        {user.address?.street_number && `${user.address.street_number} `}
                        {user.address?.street_name || addressData.street_address}
                        {addressData.postal_code && `, ${addressData.postal_code}`} {addressData.city}
                      </p>
                      {/* Complement info (apartment, code) */}
                      {(user.address?.apartment_number || user.address?.access_code) && (
                        <p className="address-line-complement">
                          {user.address.apartment_number}
                          {user.address.apartment_number && user.address.access_code && ', '}
                          {user.address.access_code && `Code : ${user.address.access_code}`}
                        </p>
                      )}
                    </div>

                    {/* Visibility Control Section */}
                    <div className="visibility-control-section">
                      <span className="visibility-control-title">Faire varier la visibilité de mon adresse</span>
                      <div className="visibility-slider-container">
                        <span className="visibility-icon">🔒</span>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={visibilityLevel}
                          onChange={(e) => handleVisibilityChange(parseInt(e.target.value))}
                          className="visibility-slider-mini"
                          disabled={savingVisibility}
                        />
                        <span className="visibility-icon">🌍</span>
                      </div>
                    </div>

                    {/* Preview: what friends see based on visibility level */}
                    <div className="address-preview">
                      <span className="preview-label">↓ Vos amis verront :</span>
                      <span className="preview-address">
                        {(() => {
                          switch (visibilityLevel) {
                            case 5: // Full
                              return (
                                <>
                                  {user.address?.street_number && `${user.address.street_number} `}
                                  {user.address?.street_name || addressData.street_address}
                                  , {addressData.postal_code} {addressData.city}
                                  {(user.address?.apartment_number || user.address?.access_code) && (
                                    <>
                                      <br />
                                      {user.address.apartment_number}
                                      {user.address.apartment_number && user.address.access_code && ', '}
                                      {user.address.access_code && `Code : ${user.address.access_code}`}
                                    </>
                                  )}
                                </>
                              );
                            case 4: // Number
                              return `${user.address?.street_number || ''} ${user.address?.street_name || addressData.street_address}, ${addressData.city}`.trim();
                            case 3: // Street
                              return `${user.address?.street_name || addressData.street_address?.split(',')[0]}, ${addressData.city}`;
                            case 2: // District
                              return user.address?.district
                                ? `${addressData.city} ${user.address.district}`
                                : addressData.city;
                            case 1: // City only
                            default:
                              return `${addressData.city}, ${addressData.country}`;
                          }
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="profile-header">
                    <button className="btn-primary" onClick={() => setEditing(true)}>
                      {t('profile.editProfile')}
                    </button>
                  </div>
                </>
              ) : (
                // VUE PUBLIQUE - Ce que mes amis voient (selon niveau de visibilité)
                <>
                  <div className="info-section-title">Adresse</div>
                  <div className="info-item address-preview public-view">
                    <span className="info-value">
                      {(() => {
                        switch (visibilityLevel) {
                          case 5: // Full
                            return (
                              <>
                                {user.address?.street_number && `${user.address.street_number} `}
                                {user.address?.street_name || addressData.street_address}
                                {user.address?.district && ` (${user.address.district})`}
                                <br />
                                {addressData.postal_code} {addressData.city}, {addressData.country}
                              </>
                            );
                          case 4: // Number
                            return (
                              <>
                                {user.address?.street_number && `${user.address.street_number} `}
                                {user.address?.street_name || addressData.street_address}
                                <br />
                                {addressData.city}, {addressData.country}
                              </>
                            );
                          case 3: // Street
                            return `${user.address?.street_name || addressData.street_address?.split(',')[0]}, ${addressData.city}`;
                          case 2: // District
                            return user.address?.district
                              ? `${user.address.district}, ${addressData.city}`
                              : addressData.city;
                          case 1: // City only
                          default:
                            return `${addressData.city}, ${addressData.country}`;
                        }
                      })()}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : (
            // MODE ÉDITION
            <>
              <div className="form-group">
                <label>{t('profile.email')}</label>
                <input type="email" value={user.email} disabled className="input-disabled" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('profile.firstName')}</label>
                  <input
                    type="text"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleProfileChange}
                  />
                </div>

                <div className="form-group">
                  <label>{t('profile.lastName')}</label>
                  <input
                    type="text"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('profile.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                />
              </div>

              <h3>{t('profile.address')}</h3>

              <div className="form-group">
                <label>{t('profile.streetAddress')}</label>
                <input
                  type="text"
                  name="street_address"
                  value={addressData.street_address}
                  onChange={handleAddressChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('profile.city')}</label>
                  <input
                    type="text"
                    name="city"
                    value={addressData.city}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label>{t('profile.state')}</label>
                  <input
                    type="text"
                    name="state"
                    value={addressData.state}
                    onChange={handleAddressChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('profile.postalCode')}</label>
                  <input
                    type="text"
                    name="postal_code"
                    value={addressData.postal_code}
                    onChange={handleAddressChange}
                  />
                </div>

                <div className="form-group">
                  <label>{t('profile.country')}</label>
                  <input
                    type="text"
                    name="country"
                    value={addressData.country}
                    onChange={handleAddressChange}
                  />
                </div>
              </div>

              <h3>Informations complémentaires</h3>
              <p className="complement-hint">Ces informations ne sont visibles que par vos amis au niveau 5 (Accès complet)</p>

              <div className="form-row">
                <div className="form-group">
                  <label>Appartement / Étage / Bâtiment</label>
                  <input
                    type="text"
                    name="apartment_number"
                    value={addressData.apartment_number || ''}
                    onChange={handleAddressChange}
                    placeholder="Ex: Bât. A, 3ème étage, porte 12"
                  />
                </div>

                <div className="form-group">
                  <label>Code d'accès / Digicode</label>
                  <input
                    type="text"
                    name="access_code"
                    value={addressData.access_code || ''}
                    onChange={handleAddressChange}
                    placeholder="Ex: A1234B"
                  />
                </div>
              </div>
            </>
          )}

          {editing && (
            <div className="profile-actions">
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? t('profile.saving') : t('profile.save')}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setEditing(false)}
                disabled={loading}
              >
                {t('profile.cancel')}
              </button>
            </div>
          )}

          {/* Temporary Locations Section */}
          <div className="temp-locations-section">
            <div className="profile-header">
              <h3>{viewMode === 'private' ? 'Mes adresses temporaires' : 'Adresses temporaires'}</h3>
            </div>

            <div className="temp-locations-list">
              {(() => {
                // In public view, only show locations visible to all friends
                const displayLocations = viewMode === 'public'
                  ? tempLocations.filter(loc => loc.visibility_type === 'all' && loc.is_active)
                  : tempLocations;

                if (displayLocations.length === 0) {
                  return (
                    <p className="no-temp-locations">
                      {viewMode === 'private' ? 'Aucun lieu temporaire créé' : 'Aucun lieu temporaire visible'}
                    </p>
                  );
                }

                return displayLocations.map((loc) => {
                  const typeInfo = getLocationTypeInfo(loc.type);
                  const startDate = new Date(loc.start_datetime);
                  const endDate = new Date(loc.end_datetime);
                  const duration = calculateDuration(startDate, endDate);

                  return (
                    <div key={loc.id} className={`temp-location-card ${loc.is_active ? 'active' : 'inactive'}`} style={{ position: 'relative' }}>
                      <div className="card-header-row">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <h4 className="location-title">
                              {typeInfo.icon} {loc.name}
                            </h4>
                          </div>
                          {loc.address && (
                            <p className="location-address">
                              {loc.address}
                            </p>
                          )}
                          {!loc.address && (
                            <p className="location-address">
                              Position enregistrée
                            </p>
                          )}
                        </div>
                        {viewMode === 'private' && (
                          <div className="card-menu">
                            <button
                              className="menu-button"
                              onClick={() => setMenuOpenId(menuOpenId === loc.id ? null : loc.id)}
                            >
                              ⋮
                            </button>
                            {menuOpenId === loc.id && (
                              <div className="menu-dropdown">
                                <button onClick={() => { handleEditTempLocation(loc.id); setMenuOpenId(null); }}>
                                  Modifier la fiche
                                </button>
                                <button onClick={() => { handleDeleteTempLocation(loc.id); setMenuOpenId(null); }} className="delete-option">
                                  Supprimer la fiche
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {loc.description && (
                        <p className="location-description">{loc.description}</p>
                      )}

                      <p className="location-dates">
                        Du {formatDate(startDate)} au {formatDate(endDate)} ({duration})
                      </p>

                      {/* Visibility badge - only in private view */}
                      {viewMode === 'private' && (
                        <div className="location-visibility">
                          {loc.visibility_type === 'all' && (
                            <span className="visibility-badge all">🌍 Tout le monde</span>
                          )}
                          {loc.visibility_type === 'groups' && loc.visible_group_ids && loc.visible_group_ids.length > 0 && (
                            <span className="visibility-badge group">👥 Groupes sélectionnés</span>
                          )}
                          {loc.visibility_type === 'friends' && loc.visible_friend_ids && loc.visible_friend_ids.length > 0 && (
                            <span className="visibility-badge friends">👤 Amis sélectionnés</span>
                          )}
                        </div>
                      )}

                      {/* Time status badge */}
                      <span
                        className="time-status-badge"
                        style={{
                          position: 'absolute',
                          bottom: '10px',
                          right: viewMode === 'private' ? '75px' : '10px',
                          backgroundColor: getTimeStatus(startDate, endDate).color,
                          color: 'white',
                          padding: '6px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          height: '26px'
                        }}
                      >
                        {getTimeStatus(startDate, endDate).label}
                      </span>

                      {/* Toggle actif/inactif - only in private view */}
                      {viewMode === 'private' && (
                        <button
                          className={`temp-location-toggle ${loc.is_active ? 'active' : 'inactive'}`}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await temporaryLocationsAPI.update(loc.id, { ...loc, is_active: !loc.is_active });
                              loadTempLocations();
                            } catch (error) {
                              console.error('Failed to toggle location status:', error);
                            }
                          }}
                          title={loc.is_active ? 'Désactiver' : 'Activer'}
                        >
                          <div className="toggle-slider"></div>
                        </button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>

            {/* Button to add new temporary location - only in private view */}
            {viewMode === 'private' && (
              <div className="add-temp-location-container">
                <button
                  className="btn-primary"
                  onClick={handleCreateNewTempLocation}
                >
                  Ajouter une adresse temporaire
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;
